import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';

import type { MessageArtifact } from '@/stores/useAppStore';
import { api } from '@shared/services/api';

import { processConversationEvents } from './conversationReplay';

import type { ActivityStep, StoredActivityStep } from '../chat/utils/activitySteps';

// Replay lives in its own module; re-exported so existing importers keep working.
export { processConversationEvents };

dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);

// Event listeners
export type ConversationActivatedListener = () => void;
const conversationActivatedListeners: ConversationActivatedListener[] = [];

/**
 * Register a listener that fires whenever a conversation is activated.
 *
 * @param listener - Callback to invoke on conversation activation.
 * @returns An unsubscribe function that removes the listener.
 */
export const onConversationActivated = (listener: ConversationActivatedListener): (() => void) => {
  conversationActivatedListeners.push(listener);
  return () => {
    const index = conversationActivatedListeners.indexOf(listener);
    if (index > -1) conversationActivatedListeners.splice(index, 1);
  };
};

const notifyConversationActivated = (): void => {
  conversationActivatedListeners.forEach(l => l());
};

// ——— Types ———
export interface Conversation {
  id: string;
  title: string;
  summary?: string;
  date?: string;
  lastMessageAt?: string;
  isActive: boolean;
  isStarred: boolean;
  /**
   * The LLM model bound to this conversation, or `null` for "Auto" routing.
   * `undefined` means the field was not present in the API response (treat
   * as "Auto" / fall back to the user's default).
   */
  modelId?: string | null;
}

export interface StoredCitation {
  text: string;
  index: number;
  is_short: boolean;
}

export interface ConversationEvent {
  type:
    | 'user_message'
    | 'text_content'
    | 'create_file'
    | 'document_created'
    | 'user_attachment'
    | 'activity'
    // Emitted by the server for standalone artifacts; never attached on replay.
    | 'artifact_complete';
  content?: string;
  messageRole?: 'user' | 'assistant';
  timestamp?: string;
  interrupted?: boolean;
  message_id?: string;
  citations?: StoredCitation[];
  /** Stored tool activity for an assistant turn (activity events). */
  steps?: StoredActivityStep[];
  started_at_ms?: number;
  ended_at_ms?: number;
  step_count?: number;
  truncated?: boolean;
  /** Per-turn render format on text_content replay ('markdown' default or 'html'). */
  format?: 'markdown' | 'html';
  /** Artifact id (create_file, document_created, user_attachment events) */
  artifact_id?: string;
  filename?: string;
  mime_type?: string;
  size?: number;
  /** Display name (document_created and user_attachment events) */
  name?: string;
  kind?: 'markdown' | 'code' | 'html';
  language?: string | null;
  body?: string;
  /** Image URLs of a user attachment (user_attachment events), computed by the server. */
  thumb_url?: string;
  orig_url?: string;
}

export interface FileArtifactInfo {
  artifact_id: string;
  filename: string;
  mime_type: string;
  size: number;
}

export interface ProcessedMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
  interrupted?: boolean;
  message_id?: string;
  citations?: StoredCitation[];
  fileArtifacts?: FileArtifactInfo[];
  /**
   * Artifacts shown on this message: documents on an assistant turn
   * (document_created events), attached images on a user turn (user_attachment events).
   */
  artifacts?: MessageArtifact[];
  /** Per-turn render format for an assistant message ('markdown' or 'html'). */
  format?: 'markdown' | 'html';
  /** Tool activity restored from history; renders the same timeline as a live turn. */
  activity?: ActivityStep[];
  turnStartedAt?: number;
  turnEndedAt?: number;
  /** True step count, which exceeds `activity.length` only on a truncated record. */
  activityStepCount?: number;
  /** The stored record was shed to fit its ceiling: steps, sources, or both. */
  activityTruncated?: boolean;
}

/** The restored-activity fields a message adapter copies off a `ProcessedMessage`. */
export type RestoredActivityFields = Pick<
  ProcessedMessage,
  'activity' | 'turnStartedAt' | 'turnEndedAt' | 'activityStepCount' | 'activityTruncated'
>;

export interface ProcessedConversation {
  messages: ProcessedMessage[];
}

interface ServerConversation {
  id: string;
  title: string;
  summary?: string;
  created_at: string;
  updated_at: string;
  is_active: string | boolean;
  is_starred?: boolean;
  user_id: string;
  persona_id: string;
  is_deleted: boolean;
  model_id?: string | null;
}

interface ConversationsResponse {
  success: boolean;
  data: ServerConversation[];
}

interface PaginatedConversationsResponse {
  success: boolean;
  data: ServerConversation[];
  pagination: {
    page: number;
    page_size: number;
    total_items: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
  message?: string;
}

interface ConversationEventsResponse {
  success: boolean;
  data: { events: ConversationEvent[] };
}

interface DeleteResponse {
  success: boolean;
  message: string;
}
interface ActivateResponse {
  success: boolean;
  message: string;
}

const transformServerConversation = (server: ServerConversation): Conversation => ({
  id: server.id,
  title: server.title,
  ...(server.summary !== undefined && { summary: server.summary }),
  date: server.created_at,
  lastMessageAt: server.updated_at,
  isActive: server.is_active === '1' || server.is_active === true,
  isStarred: server.is_starred === true,
  ...(server.model_id !== undefined && { modelId: server.model_id }),
});

/**
 * Fetch all conversations from the server (non-paginated).
 *
 * @returns An array of transformed Conversation objects.
 * @throws Error if the API response indicates failure.
 */
export const getAllConversations = async (): Promise<Conversation[]> => {
  const response = await api.get<ConversationsResponse>('/api/conversations');
  if (!response.data.success) throw new Error('Failed to get conversations');
  return response.data.data.map(transformServerConversation);
};

/**
 * Fetch a paginated list of conversations from the server.
 *
 * @param page - Page number (1-based, defaults to 1).
 * @param pageSize - Number of items per page (defaults to 25).
 * @returns An object with the `conversations` array and `pagination` metadata.
 * @throws Error if the API response indicates failure.
 */
export const getConversationsPaginated = async (
  page = 1,
  pageSize = 25
): Promise<{
  conversations: Conversation[];
  pagination: PaginatedConversationsResponse['pagination'];
}> => {
  const response = await api.get<PaginatedConversationsResponse>(
    `/api/conversations/?page=${page}&page_size=${pageSize}`
  );
  if (!response.data.success) throw new Error('Failed to get conversations');
  return {
    conversations: response.data.data.map(transformServerConversation),
    pagination: response.data.pagination,
  };
};

/**
 * Search conversations by query string with pagination.
 *
 * @param query - The search query text.
 * @param page - Page number (1-based, defaults to 1).
 * @param pageSize - Number of items per page (defaults to 25).
 * @returns An object with the matching `conversations` array and `pagination` metadata.
 * @throws Error if the API response indicates failure.
 */
export const searchConversations = async (
  query: string,
  page = 1,
  pageSize = 25
): Promise<{
  conversations: Conversation[];
  pagination: PaginatedConversationsResponse['pagination'];
}> => {
  const response = await api.get<PaginatedConversationsResponse>(
    `/api/conversations/search?q=${encodeURIComponent(query)}&page=${page}&page_size=${pageSize}`
  );
  if (!response.data.success) throw new Error('Failed to search conversations');
  return {
    conversations: response.data.data.map(transformServerConversation),
    pagination: response.data.pagination,
  };
};

/**
 * Soft-delete a conversation by ID.
 *
 * @param id - The conversation ID to delete.
 * @throws Error if the API response indicates failure.
 */
export const deleteConversation = async (id: string): Promise<void> => {
  const response = await api.delete<DeleteResponse>(`/api/conversations/${id}`);
  if (!response.data.success) throw new Error(`Failed to delete ${id}`);
};

/**
 * Activate a conversation by ID and notify all registered activation listeners.
 *
 * @param id - The conversation ID to activate.
 * @throws Error if the API response indicates failure.
 */
export const activateConversation = async (id: string): Promise<void> => {
  const response = await api.put<ActivateResponse>(`/api/conversations/${id}/activate`, {});
  if (!response.data.success) throw new Error(`Failed to activate ${id}`);
  notifyConversationActivated();
};

interface ToggleStarResponse {
  success: boolean;
  data: { id: string; is_starred: boolean };
}

/**
 * Toggle the starred status of a conversation.
 *
 * @param id - The conversation ID to star or unstar.
 * @returns The new `is_starred` boolean value after toggling.
 * @throws Error if the API response indicates failure.
 */
export const toggleStar = async (id: string): Promise<boolean> => {
  const response = await api.put<ToggleStarResponse>(`/api/conversations/${id}/star`, {});
  if (!response.data.success) throw new Error(`Failed to toggle star for ${id}`);
  return response.data.data.is_starred;
};

interface UpdateTitleResponse {
  success: boolean;
  data: { id: string; title: string };
}

/**
 * Rename a conversation by setting an explicit title.
 *
 * @param id - The conversation ID to rename.
 * @param title - The new title to assign.
 * @returns The persisted title returned by the server.
 * @throws Error if the API response indicates failure.
 */
export const updateConversationTitle = async (id: string, title: string): Promise<string> => {
  const response = await api.put<UpdateTitleResponse>(`/api/conversations/${id}/title`, { title });
  if (!response.data.success) throw new Error(`Failed to update title for ${id}`);
  return response.data.data.title;
};

/**
 * Regenerate a conversation's title from its content via the server.
 *
 * @param id - The conversation ID whose title should be regenerated.
 * @returns The newly generated title.
 * @throws Error if the API response indicates failure.
 */
export const regenerateConversationTitle = async (id: string): Promise<string> => {
  const response = await api.post<UpdateTitleResponse>(
    `/api/conversations/${id}/regenerate-title`,
    {}
  );
  if (!response.data.success) throw new Error(`Failed to regenerate title for ${id}`);
  return response.data.data.title;
};

/** Format a date based on how recently it occurred, with style depending on mode. */
function formatByRecency(date: dayjs.Dayjs, relative: boolean): string {
  const now = dayjs();
  const diffHours = now.diff(date, 'hour');

  if (relative) {
    if (diffHours < 24) return date.fromNow();
    if (diffHours < 24 * 7) return date.format('dddd [at] h:mm A');
    return date.format('MMM D, YYYY');
  }

  if (diffHours < 24) return date.format('h:mm A');
  if (diffHours < 24 * 7) return date.format('ddd h:mm A');
  return date.format('MMM D, YYYY');
}

/**
 * Format a date string for display, adapting the output based on recency.
 *
 * Within 24 hours shows relative time or hour format, within a week shows
 * the weekday, and older dates show month/day/year.
 *
 * @param dateString - The ISO date string to format, or undefined.
 * @param options - Optional formatting options.
 * @param options.relative - When true, uses relative time format (e.g. "2 hours ago").
 * @returns The formatted date string, or empty string if input is undefined.
 */
export const formatDate = (
  dateString: string | undefined,
  options: { relative?: boolean } = {}
): string => {
  if (!dateString) return '';
  const { relative = false } = options;
  try {
    const date = dayjs(dateString);
    if (!date.isValid()) return 'Invalid Date';
    return formatByRecency(date, relative);
  } catch {
    return 'Invalid Date';
  }
};

/**
 * Fetch the raw event stream for a conversation.
 *
 * @param conversationId - The conversation ID to fetch events for.
 * @returns An array of ConversationEvent objects in chronological order.
 * @throws Error if the API response indicates failure.
 */
export const getConversationEvents = async (
  conversationId: string
): Promise<ConversationEvent[]> => {
  const response = await api.get<ConversationEventsResponse>(
    `/api/conversations/${conversationId}/events`
  );
  if (!response.data.success) throw new Error('Failed to get events');
  return response.data.data.events;
};

/**
 * Load and process a conversation's events in one step.
 *
 * Fetches the event stream for the given conversation and processes it into
 * a structured message list ready for display.
 *
 * @param conversationId - The conversation ID to load.
 * @returns A ProcessedConversation with the assembled messages array.
 */
export const loadConversationInstantly = async (
  conversationId: string
): Promise<ProcessedConversation> => {
  const events = await getConversationEvents(conversationId);
  return processConversationEvents(events);
};

export default {
  getAllConversations,
  getConversationsPaginated,
  deleteConversation,
  activateConversation,
  toggleStar,
  updateConversationTitle,
  regenerateConversationTitle,
  formatDate,
  getConversationEvents,
  processConversationEvents,
  loadConversationInstantly,
};
